/**
 * Super Admin Firebase - Super Admin 전용 Firebase 인스턴스
 * 
 * 일반 유저와 완전히 분리된 Firebase Auth 세션 유지
 * Custom Token 만료 시 자동 재로그인으로 세션 유지
 */

import { FirebaseApp, getApps, initializeApp, deleteApp } from 'firebase/app';
import {
    Auth,
    signInWithCustomToken,
    signOut,
    onAuthStateChanged,
    User,
    initializeAuth,
    inMemoryPersistence
} from 'firebase/auth';
import { Firestore, getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { Functions, getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { connectAuthEmulator } from 'firebase/auth';
import { firebaseConfig } from '../const/Settings';
import { configureAppCheck } from './AppCheck';
import { Util } from './Utils/Util';

const env = import.meta.env;

const ADMIN_APP_NAME = 'super-admin';
const FB_REGION = 'asia-northeast1';
const TOKEN_STORAGE_KEY = 'nextpod_admin_token';
const CREDENTIALS_STORAGE_KEY = 'nextpod_admin_creds';
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45분마다 토큰 갱신

interface AdminCredentials {
    email: string;
    password: string;  // Base64 encoded
}

class SuperAdminFirebase {
    private static instance: SuperAdminFirebase | null = null;

    private app: FirebaseApp;
    private _auth: Auth;
    private _store: Firestore;
    private _functions: Functions;
    private _currentUser: User | null = null;
    private _isReady: boolean = false;
    private _readyPromise: Promise<void>;
    private _readyResolve: (() => void) | null = null;
    private _refreshTimer: ReturnType<typeof setInterval> | null = null;

    private constructor() {
        // 기존 admin 앱이 있으면 삭제 후 재생성 (clean state)
        const existingApp = getApps().find(app => app.name === ADMIN_APP_NAME);
        if (existingApp) {
            deleteApp(existingApp).catch(() => { });
        }

        // 새로운 Firebase 인스턴스 생성
        this.app = initializeApp(firebaseConfig, ADMIN_APP_NAME);

        // 앱 인스턴스별로 App Check 필요 — 없으면 identitytoolkit/firestore enforcement 시
        // 슈퍼관리자 로그인·조회가 미인증으로 차단된다
        configureAppCheck(this.app);

        // inMemoryPersistence 사용 - 일반 유저 세션에 영향 없음
        this._auth = initializeAuth(this.app, {
            persistence: inMemoryPersistence
        });

        this._store = getFirestore(this.app);
        this._functions = getFunctions(this.app, FB_REGION);

        // 에뮬레이터 연결 (로컬 개발 환경)
        if (Util.isLocalDevelopment()) {
            const emulatorHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
            if (env.VITE_USE_EMULATOR_AUTH) {
                connectAuthEmulator(this._auth, `http://${emulatorHost}:9099`);
            }
            if (env.VITE_USE_EMULATOR_FIRESTORE) {
                connectFirestoreEmulator(this._store, emulatorHost, 8080);
            }
            if (env.VITE_USE_EMULATOR_FUNCTIONS) {
                connectFunctionsEmulator(this._functions, emulatorHost, 5001);
            }
        }

        // Ready promise 설정
        this._readyPromise = new Promise(resolve => {
            this._readyResolve = resolve;
        });

        // Auth 상태 감시
        onAuthStateChanged(this._auth, (user) => {
            this._currentUser = user;

            if (user) {
                this.startTokenRefresh();
            } else {
                this.stopTokenRefresh();
            }

            // 인증 상태가 없고, 저장된 자격증명이 있으면 자동 재로그인 시도
            if (!user && !this._isReady) {
                this.tryAutoLogin().finally(() => {
                    this._isReady = true;
                    if (this._readyResolve) {
                        this._readyResolve();
                        this._readyResolve = null;
                    }
                });
            } else {
                this._isReady = true;
                if (this._readyResolve) {
                    this._readyResolve();
                    this._readyResolve = null;
                }
            }
        });
    }

    /**
     * 저장된 자격증명으로 자동 재로그인 시도
     */
    private async tryAutoLogin(): Promise<void> {
        // 먼저 저장된 토큰으로 시도
        const savedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        if (savedToken) {
            try {
                console.log('SuperAdminFirebase: Attempting auto-login with saved token');
                await signInWithCustomToken(this._auth, savedToken);
                console.log('SuperAdminFirebase: Auto-login with token successful');
                return;
            } catch (error) {
                console.warn('SuperAdminFirebase: Token expired, trying with credentials');
            }
        }

        // 토큰이 만료되었으면 자격증명으로 새 토큰 요청
        const savedCreds = sessionStorage.getItem(CREDENTIALS_STORAGE_KEY);
        if (savedCreds) {
            try {
                const creds: AdminCredentials = JSON.parse(savedCreds);
                const password = atob(creds.password); // Base64 decode

                console.log('SuperAdminFirebase: Requesting new token with credentials');
                const newToken = await this.requestNewToken(creds.email, password);

                if (newToken) {
                    sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
                    await signInWithCustomToken(this._auth, newToken);
                    console.log('SuperAdminFirebase: Auto-login with new token successful');
                    return;
                }
            } catch (error) {
                console.warn('SuperAdminFirebase: Auto-login failed, clearing credentials', error);
                this.clearStoredData();
            }
        }
    }

    /**
     * 새 토큰 요청
     */
    private async requestNewToken(email: string, password: string): Promise<string | null> {
        try {
            const superAdminLogin = httpsCallable(this._functions, 'superAdminLogin', { timeout: 30000 });
            const result = await superAdminLogin({ email, password });
            const data = result.data as { success: boolean; token: string };

            if (data.success && data.token) {
                return data.token;
            }
        } catch (error) {
            console.error('SuperAdminFirebase: Failed to request new token', error);
        }
        return null;
    }

    /**
     * 토큰 자동 갱신 시작
     */
    private startTokenRefresh(): void {
        this.stopTokenRefresh();

        this._refreshTimer = setInterval(async () => {
            const savedCreds = sessionStorage.getItem(CREDENTIALS_STORAGE_KEY);
            if (savedCreds) {
                try {
                    const creds: AdminCredentials = JSON.parse(savedCreds);
                    const password = atob(creds.password);

                    console.log('SuperAdminFirebase: Refreshing token...');
                    const newToken = await this.requestNewToken(creds.email, password);

                    if (newToken) {
                        sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
                        // ID Token도 갱신
                        if (this._currentUser) {
                            await this._currentUser.getIdToken(true);
                        }
                        console.log('SuperAdminFirebase: Token refreshed successfully');
                    }
                } catch (error) {
                    console.error('SuperAdminFirebase: Token refresh failed', error);
                }
            }
        }, TOKEN_REFRESH_INTERVAL);

        console.log('SuperAdminFirebase: Token refresh timer started (every 45 min)');
    }

    /**
     * 토큰 자동 갱신 중지
     */
    private stopTokenRefresh(): void {
        if (this._refreshTimer) {
            clearInterval(this._refreshTimer);
            this._refreshTimer = null;
        }
    }

    /**
     * 저장된 데이터 삭제
     */
    private clearStoredData(): void {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(CREDENTIALS_STORAGE_KEY);
    }

    static getInstance(): SuperAdminFirebase {
        if (!SuperAdminFirebase.instance) {
            SuperAdminFirebase.instance = new SuperAdminFirebase();
        }
        return SuperAdminFirebase.instance;
    }

    get auth(): Auth {
        return this._auth;
    }

    get store(): Firestore {
        return this._store;
    }

    get functions(): Functions {
        return this._functions;
    }

    get currentUser(): User | null {
        return this._currentUser;
    }

    get isAuthenticated(): boolean {
        return this._currentUser !== null;
    }

    /**
     * Auth 상태가 초기화될 때까지 대기
     */
    async waitForReady(): Promise<void> {
        if (this._isReady) return;
        return this._readyPromise;
    }

    /**
     * Custom Token으로 로그인 + 자격증명 저장
     */
    async signIn(customToken: string, email?: string, password?: string): Promise<User> {
        // 토큰 저장
        sessionStorage.setItem(TOKEN_STORAGE_KEY, customToken);

        // 자격증명 저장 (자동 갱신용)
        if (email && password) {
            const creds: AdminCredentials = {
                email,
                password: btoa(password)  // Base64 encode
            };
            sessionStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(creds));
        }

        const userCredential = await signInWithCustomToken(this._auth, customToken);
        await userCredential.user.getIdToken(true);
        return userCredential.user;
    }

    /**
     * 로그아웃 (Admin만 - 일반 유저 영향 없음)
     */
    async signOut(): Promise<void> {
        this.stopTokenRefresh();
        this.clearStoredData();
        await signOut(this._auth);
    }

    /**
     * Cloud Function 호출 (Super Admin Auth 사용)
     */
    getFunc(funcName: string) {
        return httpsCallable(this._functions, funcName, { timeout: 535 * 1000 });
    }
}

// 싱글톤 인스턴스 export
export const AdminFire = SuperAdminFirebase.getInstance();

/**
 * Super Admin 전용 Cloud Function 호출
 */
export function getAdminFunc(funcName: string) {
    return AdminFire.getFunc(funcName);
}
